'use client';

import { useState, useCallback, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { List, X } from 'phosphor-react';
import { Button } from '@/components/ui/button';
import { useTranslations } from '@/app/providers';
import { LanguageSwitcher } from '@/components/common/language-switcher';
import { useIsTablet } from '@/hooks/use-breakpoint';
import { cn, pushGTMEvent } from '@/lib/utils';

type MenuItem = {
  label: string;
  href: string;
};

interface NavbarProps {
  menuItems: MenuItem[];
  handleAnchorClick: (e: React.MouseEvent<HTMLAnchorElement>, href: string) => void;
}

function MobileNavbar({ menuItems, handleAnchorClick }: NavbarProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const t = useTranslations();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  const handleClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    handleAnchorClick(e, href);
    closeMenu();
  }, [handleAnchorClick, closeMenu]);

  // Block scroll when menu is open while preserving scrollbar space
  useEffect(() => {
    if (isMenuOpen) {
      // Calculate scrollbar width
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      // Apply overflow hidden and padding to preserve scrollbar space
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    } else {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [isMenuOpen]);

  // Close menu with ESC for WCAG compliance
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isMenuOpen) {
        closeMenu();
      }
    };

    if (isMenuOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isMenuOpen, closeMenu]);

  return (
    <>
      <header className='md:container md:mx-auto fixed top-8 left-6 right-6 z-50'>
        <div className='navbar-gradient-border rounded-[9.375rem]'>
          <div className='relative flex items-center justify-between px-6 py-3 backdrop-blur-sm rounded-[9.375rem]'>
            <Link href='/' className='flex items-center'>
              <Image
                src='/GeeksCreations Logo.svg'
                alt='GeeksCreations'
                width={89}
                height={40}
                className='h-10 w-auto'
                priority
              />
            </Link>

            <Button
              variant="ghost"
              onClick={toggleMenu}
              className='h-auto w-auto p-2 text-white hover:bg-white/10 relative z-50 shrink-0 [&_svg]:size-6'
              aria-label={isMenuOpen ? t.nav.closeMenu : t.nav.openMenu}
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? <X size={24} className='shrink-0' /> : <List size={24} className='shrink-0' />}
            </Button>
          </div>
        </div>
      </header>

      {isMenuOpen && (
        <div className='fixed inset-0 bg-black z-40 pt-16'>
          <div className='flex flex-col items-center justify-center h-full gap-14'>
            {menuItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                onClick={(e) => handleClick(e, item.href)}
                className='text-white text-xl hover:text-white/80 transition-colors'
              >
                {item.label}
              </Link>
            ))}
            <LanguageSwitcher />
            <ContactButton variant='mobile' onClick={closeMenu} />
          </div>
        </div>
      )}
    </>
  );
}

function DesktopNavbar({ menuItems, handleAnchorClick }: NavbarProps) {
  return (
    <header className='md:container md:mx-auto fixed top-8 left-6 right-6 z-50'>
      <div className='navbar-gradient-border rounded-[9.375rem]'>
        <div className='relative flex items-center px-6 py-3 backdrop-blur-sm rounded-[9.375rem]'>
          <Link href='/' className='flex items-center'>
            <Image
              src='/GeeksCreations Logo.svg'
              alt='GeeksCreations'
              width={89}
              height={40}
              className='h-10 w-auto'
              priority
            />
          </Link>

          <nav className='flex-1 flex items-center justify-center gap-8' aria-label='Main navigation'>
            {menuItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                onClick={(e) => handleAnchorClick(e, item.href)}
                className='text-white hover:text-white/80 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-violet-400 rounded'
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className='flex items-center'>
            <LanguageSwitcher />
            <ContactButton />
          </div>
        </div>
      </div>
    </header>
  );
}

export function NavigationMenuDemo() {
  const isTablet = useIsTablet();
  const t = useTranslations();

  const menuItems: MenuItem[] = [
    { label: t.nav.aboutUs, href: '#chi-siamo' },
    { label: t.nav.whatWeDo, href: '#cosa-facciamo' },
    { label: t.nav.howWeDoIt, href: '#come-lo-facciamo' },
    { label: t.nav.focus, href: '#focus' },
  ];

  const handleAnchorClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const targetId = href.replace('#', '');
    const element = document.getElementById(targetId);
    
    if (element) {
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  }, []);

  if (isTablet) {
    return <MobileNavbar menuItems={menuItems} handleAnchorClick={handleAnchorClick} />;
  }

  return <DesktopNavbar menuItems={menuItems} handleAnchorClick={handleAnchorClick} />;
}

function ContactButton({ variant = 'desktop', onClick }: { variant?: 'desktop' | 'mobile'; onClick?: () => void }) {
  const t = useTranslations();
  
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    
    pushGTMEvent('click_cta_navbar', {
      cta_type: 'contact',
      cta_location: variant === 'mobile' ? 'navbar_mobile' : 'navbar_desktop',
    });
    
    const element = document.getElementById('contatti');
    
    if (element) {
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
    
    if (onClick) {
      onClick();
    }
  };

  return (
    <Button asChild>
      <Link
        href='#contatti'
        onClick={handleClick}
      >
        {t.nav.contactUs}
      </Link>
    </Button>
  );
}
